import React, { useEffect, useMemo, useState } from "react";
import { Select, Text } from "@foris/avocado-suite";
import { useTranslation } from "react-i18next";
import { useGetExternalReferences } from "@services/collections/references/referencesServices";
import { ExternalReference } from "@models/ISchema";
import useDebounce from "@common/hooks/useDebounce";
import {
  getExternalReferenceGroupDetails,
  getExternalReferencePrimaryLine,
  getExternalReferenceSubtitle,
} from "@utils/externalReferences";
import styles from "./groupExternalCrnSelector.module.scss";

interface GroupExternalCrnSelectorProps {
  scenarioId: string;
  originId: string;
  termCode: string;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  onChange?: (option: { label: string; value: string; self: ExternalReference }) => void;
}

const GroupExternalCrnSelector = ({
  scenarioId,
  originId,
  termCode,
  label,
  placeholder,
  disabled = false,
  onChange,
}: GroupExternalCrnSelectorProps) => {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearchTerm = useDebounce(searchTerm, 500);
  const { handleGetExternalReferences, loading, data } = useGetExternalReferences();

  const optionLabels = useMemo(
    () => ({
      term: t("group-references.group-selector.option.term"),
      campus: t("group-references.group-selector.option.campus"),
      status: t("group-references.group-selector.option.status"),
      active: t("group-references.group-selector.option.active"),
      inactive: t("group-references.group-selector.option.inactive"),
    }),
    [t],
  );

  useEffect(() => {
    if (debouncedSearchTerm) {
      handleGetExternalReferences({
        scenarioId,
        originId,
        crn: debouncedSearchTerm,
        term: termCode,
      });
    }
  }, [debouncedSearchTerm]);

  const crnOptions = useMemo(() => {
    if (loading || !data?.length) return [];

    return data?.map(externalReference => ({
      label: getExternalReferencePrimaryLine(externalReference),
      value: externalReference?.referenceKey,
      self: externalReference,
    }));
  }, [data, loading]);

  return (
    <Select
      className={styles.groupExternalCrnSelector}
      label={label ?? t("group-references.group-selector.crn-label")}
      placeholder={placeholder ?? t("group-references.group-selector.crn-placeholder")}
      formatOptionLabel={(data: any) => {
        const externalReference: ExternalReference = data?.self;
        const referenceKey = externalReference?.referenceKey;
        const groupDetails = getExternalReferenceGroupDetails(externalReference);

        return (
          <div className={styles.groupExternalCrnSelector_option}>
            <Text className={styles.groupExternalCrnSelector_optionLabel}>
              {!!referenceKey && (
                <span className={styles.groupExternalCrnSelector_optionLabelKey}>
                  {referenceKey}
                </span>
              )}
              {!!referenceKey && !!groupDetails && " - "}
              {groupDetails}
            </Text>

            <Text className={styles.groupExternalCrnSelector_optionTerm} type="sm">
              {getExternalReferenceSubtitle(externalReference, optionLabels)}
            </Text>
          </div>
        );
      }}
      options={crnOptions}
      value={searchTerm}
      onInputChange={setSearchTerm}
      onChange={onChange}
      disabled={disabled}
      isLoading={loading}
    />
  );
};

export default GroupExternalCrnSelector;
